// Tutor SessionsPage — two views depending on whether it was reached with a
// ?courseId= filter (from a course card's "View sessions" link) or directly
// from the nav:
//   - filtered: flat table for just that course, with a back link
//   - unfiltered: sessions grouped under each course's own heading
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCourses } from "../../contexts/CoursesContext.js";
import { useSessions } from "../../contexts/SessionsContext.js";
import Badge from "../../components/shared/Badge.js";
import { IconArrowLeft } from "../../components/shared/icons.js";
import { formatDateTime } from "../../lib/formatDateTime.js";
import type { SessionWithCourse } from "@dm-lms/shared/types";

function SessionsTable({ sessions }: { sessions: SessionWithCourse[] }) {
  const now = Date.now();
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-dm-slate">
          <tr>
            <th className="px-4 py-3 font-medium">Title</th>
            <th className="px-4 py-3 font-medium">Scheduled</th>
            <th className="px-4 py-3 font-medium">Mode</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sessions.map((session) => {
            const isUpcoming = new Date(session.scheduledAt).getTime() > now;
            return (
              <tr key={session.id}>
                <td className="px-4 py-3 font-medium text-dm-navy">{session.title}</td>
                <td className="px-4 py-3">{formatDateTime(session.scheduledAt)}</td>
                <td className="px-4 py-3">
                  <Badge tone="gray">{session.mode}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={isUpcoming ? "blue" : "gray"}>{isUpcoming ? "Upcoming" : "Past"}</Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/app/tutor/sessions/${session.id}`}
                    className="text-xs px-3 py-1.5 rounded-md bg-dm-blue text-white inline-block"
                  >
                    View session
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SessionsPage() {
  const [searchParams] = useSearchParams();
  const courseIdFilter = searchParams.get("courseId");

  const { courses: myCourses } = useCourses();
  const myCourseIds = useMemo(() => new Set(myCourses.map((course) => course.id)), [myCourses]);

  const { sessions: sessionsData, isLoading, error } = useSessions();

  const mySessions = useMemo(
    () => sessionsData.filter((session) => myCourseIds.has(session.courseId)),
    [sessionsData, myCourseIds]
  );

  // Flat, filtered view — one course, newest first.
  const filteredSessions = useMemo(() => {
    if (!courseIdFilter) return [];
    return mySessions
      .filter((session) => session.courseId === courseIdFilter)
      .sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());
  }, [mySessions, courseIdFilter]);

  const filterCourseName = courseIdFilter
    ? (mySessions.find((s) => s.courseId === courseIdFilter)?.courseTitle ?? "Course")
    : null;

  // Grouped view — one group per course, sessions within a group newest
  // first, groups themselves ordered by their most recent session.
  const groupedByCourse = useMemo(() => {
    if (courseIdFilter) return [];
    const map = new Map<string, SessionWithCourse[]>();
    for (const session of mySessions) {
      const list = map.get(session.courseId) ?? [];
      list.push(session);
      map.set(session.courseId, list);
    }
    const groups = Array.from(map.values()).map((sessions) => {
      const sorted = [...sessions].sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());
      return {
        courseId: sorted[0]!.courseId,
        courseTitle: sorted[0]!.courseTitle,
        mode: sorted[0]!.mode,
        sessions: sorted,
      };
    });
    groups.sort((a, b) => new Date(b.sessions[0]!.scheduledAt).getTime() - new Date(a.sessions[0]!.scheduledAt).getTime());
    return groups;
  }, [mySessions, courseIdFilter]);

  return (
    <div className="dm-page">
      {courseIdFilter && (
        <Link
          to="/app/tutor/courses"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-dm-blue hover:underline"
        >
          <IconArrowLeft width={14} height={14} />
          Back to courses
        </Link>
      )}

      <h1 className="text-2xl font-semibold text-dm-navy mb-6">
        {courseIdFilter ? `${filterCourseName} — Sessions` : "Sessions"}
      </h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading sessions…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && courseIdFilter && (
        filteredSessions.length === 0 ? (
          <p className="text-dm-slate text-sm">No sessions yet.</p>
        ) : (
          <SessionsTable sessions={filteredSessions} />
        )
      )}

      {!isLoading && !error && !courseIdFilter && (
        groupedByCourse.length === 0 ? (
          <p className="text-dm-slate text-sm">No sessions yet.</p>
        ) : (
          <div className="space-y-8">
            {groupedByCourse.map((group) => (
              <section key={group.courseId}>
                <div className="mb-3 flex items-center gap-2">
                  <h2 className="text-lg font-medium text-dm-navy">{group.courseTitle}</h2>
                  <Badge tone="gray">{group.mode}</Badge>
                </div>
                <SessionsTable sessions={group.sessions} />
              </section>
            ))}
          </div>
        )
      )}
    </div>
  );
}
